// Findings-review packet: the bounded, deduplicated input one review reads.
//
// Three independent inputs meet here. A SELECTION says which findings the
// analyst means to review and how that set was chosen. The supplied FINDINGS
// and EVIDENCE are what the calling surface already read through authorized
// requests. ATTRIBUTION says which endpoint and AI agent type each Prompt
// Session belongs to, where that is known. The builder never fetches: a packet
// built from fixtures and a packet built from live reads go through the same
// code, so coverage and duplicate handling can be tested without a model.
//
// The model receives compact aliases (F1, E1, S1, EP1). The packet keeps the
// lookup from alias to real identity so a result can be validated and the UI
// can navigate to the original finding or evidence.

export const PACKET_VERSION = 1;
export const SELECTION_TYPES = ["prompt_session", "endpoint", "collection"];
export const TIME_BASES = ["review_completed", "source_observed"];
export const MAX_SELECTORS = 50;
export const MAX_FINDINGS = 250;
export const MAX_QUOTE_CHARS = 400;
export const MAX_RATIONALE_CHARS = 700;
// Evaluation starting point, chosen small on purpose. The shipping cap comes
// from measured packet size and review quality, not from model capacity.
export const DEFAULT_MAX_PACKET_BYTES = 120_000;

const FILTERABLE_FIELDS = new Set([
    "reason", "evidenceStatus", "behaviourSeverity", "behaviourCategory",
    "contentSensitivity", "contentCategory", "policyStatus", "normStatus", "intentStatus", "verifierStatus",
]);

const str = (v) => (v == null ? "" : String(v));

// 53-bit string hash (cyrb53). Used for content fingerprints and packet
// digests; length-prefixed by callers where a collision must not hide a change.
export function fingerprint(input) {
    const s = String(input ?? "");
    let h1 = 0xdeadbeef;
    let h2 = 0x41c6ce57;
    for (let i = 0; i < s.length; i++) {
        const ch = s.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return `${s.length}:${(4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36)}`;
}

// Quote text as compared for duplicate detection: escaped line breaks, runs
// of whitespace, and surrounding quote marks do not make two captures differ.
export function normalizeQuote(s) {
    return str(s)
        .replace(/\\+[nrt]/g, " ")
        .replace(/\s+/g, " ")
        .replace(/^["'\u201c\u201d\u2026\s]+/, "")
        .replace(/["'\u201c\u201d\u2026\s]+$/, "")
        .trim();
}

export function findingIdentity(f) {
    return `${str(f?.traceId)}/${str(f?.reportId)}/${str(f?.findingId)}`;
}

function uniqueStrings(list, label) {
    const out = [];
    const seen = new Set();
    for (const v of list ?? []) {
        const s = str(v).trim();
        if (!s) throw new Error(`${label} contains an empty identifier.`);
        if (!seen.has(s)) { seen.add(s); out.push(s); }
    }
    return out;
}

function normalizeTimeRange(range, { required }) {
    if (!range) {
        if (required) throw new Error("This selection requires an explicit time interval.");
        return null;
    }
    const start = Date.parse(str(range.start));
    const end = Date.parse(str(range.end));
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) throw new Error("The time interval is invalid.");
    if (!TIME_BASES.includes(range.basis)) throw new Error("The time interval must state its timestamp basis.");
    return { start: new Date(start).toISOString(), end: new Date(end).toISOString(), basis: range.basis };
}

function normalizeFilters(filters) {
    const out = [];
    for (const f of filters ?? []) {
        if (!FILTERABLE_FIELDS.has(f?.field)) throw new Error(`Unsupported finding filter: ${str(f?.field) || "(missing field)"}.`);
        const values = uniqueStrings(Array.isArray(f.values) ? f.values : [f.value], "A finding filter").map((v) => v.toLowerCase());
        if (!values.length) throw new Error(`Finding filter ${f.field} has no values.`);
        out.push({ field: f.field, values });
    }
    return out;
}

// Turn a caller's selection into the canonical shape every later step reads.
// Throws on anything ambiguous: the reviewer must never guess what was meant.
export function normalizeSelection(input) {
    const type = input?.type;
    if (!SELECTION_TYPES.includes(type)) throw new Error("Unknown selection type.");
    const base = { type, traceIds: [], endpointIds: [], findingRefs: [], timeRange: null, filters: normalizeFilters(input.filters), label: str(input.label) };
    if (type === "prompt_session") {
        base.traceIds = uniqueStrings([input.traceId], "Prompt Session selection");
        base.timeRange = normalizeTimeRange(input.timeRange, { required: false });
    } else if (type === "endpoint") {
        base.endpointIds = uniqueStrings([input.endpointId], "Endpoint selection");
        base.timeRange = normalizeTimeRange(input.timeRange, { required: true });
    } else {
        base.traceIds = uniqueStrings(input.traceIds, "Collection Prompt Sessions");
        base.endpointIds = uniqueStrings(input.endpointIds, "Collection endpoints");
        const refs = new Map();
        for (const r of input.findingRefs ?? []) {
            if (!r?.findingId || !r?.traceId || !r?.reportId) throw new Error("A collection finding reference needs finding, Prompt Session, and report identities.");
            const ref = { findingId: str(r.findingId), traceId: str(r.traceId), reportId: str(r.reportId) };
            refs.set(findingIdentity(ref), ref);
        }
        base.findingRefs = [...refs.values()];
        base.timeRange = normalizeTimeRange(input.timeRange, { required: false });
        if (!base.traceIds.length && !base.endpointIds.length && !base.findingRefs.length) throw new Error("A collection selects nothing.");
    }
    const selectors = base.traceIds.length + base.endpointIds.length;
    if (selectors > MAX_SELECTORS) throw new Error(`A selection can name at most ${MAX_SELECTORS} Prompt Sessions and endpoints together.`);
    if (base.findingRefs.length > MAX_FINDINGS) throw new Error(`A selection can name at most ${MAX_FINDINGS} findings.`);
    return base;
}

function matchesFilters(f, filters) {
    return filters.every(({ field, values }) => values.includes(str(f[field]).toLowerCase()));
}

function inReviewWindow(f, timeRange) {
    if (!timeRange || timeRange.basis !== "review_completed") return true;
    const at = Number(f.completedAtMs);
    if (!Number.isFinite(at)) return false;
    return at >= Date.parse(timeRange.start) && at <= Date.parse(timeRange.end);
}

function matchesSelectors(f, sel) {
    if (sel.type === "prompt_session") return sel.traceIds.includes(str(f.traceId));
    if (sel.type === "endpoint") return sel.endpointIds.includes(str(f.endpointId));
    return sel.traceIds.includes(str(f.traceId))
        || (f.endpointId != null && sel.endpointIds.includes(str(f.endpointId)))
        || sel.findingRefs.some((r) => findingIdentity(r) === findingIdentity(f));
}

// Decide which supplied findings are in the review. Overlapping selectors
// admit a finding once; supplied findings outside the selection are counted,
// not silently reviewed; explicit references with no supplied body stay
// visible as unresolved.
export function resolveMembership(selection, findings) {
    const sel = selection?.type && Array.isArray(selection.traceIds) ? selection : normalizeSelection(selection);
    const byIdentity = new Map();
    let raw = 0;
    let excluded = 0;
    let outsideWindow = 0;
    for (const f of findings ?? []) {
        if (!f?.findingId || !f?.traceId || !f?.reportId) throw new Error("A supplied finding is missing its identity.");
        raw += 1;
        if (!matchesSelectors(f, sel) || !matchesFilters(f, sel.filters)) { excluded += 1; continue; }
        if (!inReviewWindow(f, sel.timeRange)) { outsideWindow += 1; continue; }
        const id = findingIdentity(f);
        if (!byIdentity.has(id)) byIdentity.set(id, f);
    }
    const ordered = [...byIdentity.values()].sort((a, b) => {
        const d = (Number(a.completedAtMs) || 0) - (Number(b.completedAtMs) || 0);
        return d !== 0 ? d : findingIdentity(a).localeCompare(findingIdentity(b));
    });
    const truncated = ordered.length > MAX_FINDINGS;
    const members = truncated ? ordered.slice(0, MAX_FINDINGS) : ordered;
    const memberIds = new Set(members.map(findingIdentity));
    const unresolvedRefs = sel.findingRefs.filter((r) => !byIdentity.has(findingIdentity(r)));
    return {
        members, rawRecords: raw, duplicateRecords: raw - excluded - outsideWindow - byIdentity.size,
        excluded, outsideWindow, unresolvedRefs, truncated, omitted: ordered.length - members.length,
        memberIds,
    };
}

export function evidenceIdentity(traceId, citation) {
    const src = citation?.source;
    if (src?.messageId && Number.isFinite(Number(src.partOrdinal))) {
        return { id: `src:${traceId}:${src.messageId}:${src.partOrdinal}:${Number(src.start) || 0}-${Number(src.end) || 0}`, located: true };
    }
    return { id: `quote:${traceId}:${fingerprint(normalizeQuote(citation?.quote))}`, located: false };
}

function clip(text, max) {
    const s = str(text);
    return s.length > max ? { text: `${s.slice(0, max)}...`, excerpt: true } : { text: s, excerpt: false };
}

// Build the packet. Pure: same inputs, same packet, same digest.
export function buildPacket({ selection, findings = [], evidence = [], attribution = {}, collectedAt = null }) {
    const sel = normalizeSelection(selection);
    const membership = resolveMembership(sel, findings);

    const sessions = new Map();
    const endpoints = new Map();
    const sessionAlias = (traceId) => {
        if (!sessions.has(traceId)) sessions.set(traceId, { alias: `S${sessions.size + 1}`, traceId, endpointIds: new Set(), agentType: str(attribution?.[traceId]?.agentType) || "unknown", findings: [] });
        return sessions.get(traceId);
    };
    const endpointAlias = (endpointId) => {
        if (!endpoints.has(endpointId)) endpoints.set(endpointId, { alias: `EP${endpoints.size + 1}`, endpointId, sessions: new Set(), findings: [] });
        return endpoints.get(endpointId);
    };

    const evidenceById = new Map();
    const contentGroups = new Map();
    const evidenceFor = (traceId, citation, findingAlias) => {
        const { id, located } = evidenceIdentity(traceId, citation);
        const normalized = normalizeQuote(citation?.quote);
        const content = fingerprint(normalized);
        if (!evidenceById.has(id)) {
            const clipped = clip(normalized, MAX_QUOTE_CHARS);
            evidenceById.set(id, {
                alias: `E${evidenceById.size + 1}`, id, traceId, located, source: located ? { ...citation.source } : null,
                text: clipped.text, excerpt: clipped.excerpt, contentFingerprint: content,
                // Report-carried quotes are what the review stored, not a fresh
                // read of the conversation. A supplied body upgrades this below.
                provenance: "report_quote", complete: false, observedAtMs: null,
                citedBy: [], statuses: new Set(), purposes: new Set(),
            });
            if (!contentGroups.has(content)) contentGroups.set(content, []);
            contentGroups.get(content).push(id);
        }
        const e = evidenceById.get(id);
        e.citedBy.push(findingAlias);
        if (citation?.status) e.statuses.add(str(citation.status).toLowerCase());
        if (citation?.purpose) e.purposes.add(str(citation.purpose).toLowerCase());
        return e.alias;
    };

    const packetFindings = membership.members.map((f, i) => {
        const alias = `F${i + 1}`;
        const traceId = str(f.traceId);
        const session = sessionAlias(traceId);
        session.findings.push(alias);
        const endpointId = str(f.endpointId || attribution?.[traceId]?.endpointId || "");
        let endpoint = null;
        if (endpointId) {
            endpoint = endpointAlias(endpointId);
            endpoint.findings.push(alias);
            endpoint.sessions.add(session.alias);
            session.endpointIds.add(endpointId);
        }
        const rationale = clip(f.rationale, MAX_RATIONALE_CHARS);
        return {
            alias, identity: findingIdentity(f), findingId: str(f.findingId), traceId, reportId: str(f.reportId),
            session: session.alias, endpoint: endpoint?.alias ?? null, agentType: session.agentType,
            title: str(f.title), rationale: rationale.text, rationaleExcerpt: rationale.excerpt,
            reason: str(f.reason), evidenceStatus: str(f.evidenceStatus), verifierStatus: str(f.verifierStatus),
            behaviourSeverity: str(f.behaviourSeverity), behaviourCategory: str(f.behaviourCategory),
            contentSensitivity: str(f.contentSensitivity), contentCategory: str(f.contentCategory),
            policy: f.policy?.status ? { status: str(f.policy.status), reference: str(f.policy.reference) } : null,
            norms: f.norms?.status ? { status: str(f.norms.status), reference: str(f.norms.reference) } : null,
            intent: f.intent?.status ? { status: str(f.intent.status), reference: str(f.intent.reference) } : null,
            completedAtMs: Number.isFinite(Number(f.completedAtMs)) ? Number(f.completedAtMs) : null,
            evidence: (f.citations ?? []).map((c) => evidenceFor(traceId, c, alias)),
        };
    });

    // Supplied evidence bodies. Matching by identity upgrades a report quote to
    // resolved evidence; anything else is included as its own object.
    let unmatchedSupplied = 0;
    for (const item of evidence) {
        if (!item?.id) throw new Error("A supplied evidence object is missing its identity.");
        const traceId = str(item.traceId);
        const match = item.source ? evidenceById.get(evidenceIdentity(traceId, item).id) : evidenceById.get(str(item.id));
        const clipped = clip(str(item.content), MAX_QUOTE_CHARS);
        if (match) {
            Object.assign(match, { provenance: "resolved", complete: item.complete !== false && !clipped.excerpt, observedAtMs: item.observedAtMs ?? null, text: clipped.text, excerpt: clipped.excerpt || item.excerpt === true });
            continue;
        }
        unmatchedSupplied += 1;
        const id = str(item.id);
        const content = fingerprint(normalizeQuote(item.content));
        evidenceById.set(id, {
            alias: `E${evidenceById.size + 1}`, id, traceId, located: Boolean(item.source), source: item.source ?? null,
            text: clipped.text, excerpt: clipped.excerpt || item.excerpt === true, contentFingerprint: content,
            provenance: str(item.provenance) || "supplied", complete: item.complete !== false && !clipped.excerpt,
            observedAtMs: item.observedAtMs ?? null, citedBy: [], statuses: new Set(), purposes: new Set(),
        });
        if (!contentGroups.has(content)) contentGroups.set(content, []);
        contentGroups.get(content).push(id);
    }

    // One marker per conversation message, so two located quotes from the same
    // message read as one message at two offsets, not two messages.
    const messages = new Map();
    const messageAlias = (e) => {
        if (!e.located || !e.source?.messageId) return null;
        const key = `${e.traceId}:${e.source.messageId}`;
        if (!messages.has(key)) messages.set(key, `M${messages.size + 1}`);
        return messages.get(key);
    };
    const packetEvidence = [...evidenceById.values()].map((e) => ({
        ...e, message: messageAlias(e), statuses: [...e.statuses].sort(), purposes: [...e.purposes].sort(),
        sameContentAs: contentGroups.get(e.contentFingerprint).filter((id) => id !== e.id).map((id) => evidenceById.get(id).alias),
    }));

    const count = (list, key) => {
        const m = new Map();
        for (const f of list) { const k = key(f) || "unknown"; m.set(k, (m.get(k) || 0) + 1); }
        return Object.fromEntries([...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])));
    };
    const distributions = {
        findingsPerSession: count(packetFindings, (f) => f.session),
        findingsPerEndpoint: count(packetFindings, (f) => f.endpoint),
        findingsPerAgentType: count(packetFindings, (f) => f.agentType),
        bySeverity: count(packetFindings, (f) => f.behaviourSeverity),
        bySensitivity: count(packetFindings, (f) => f.contentSensitivity),
        byReason: count(packetFindings, (f) => f.reason),
        byBehaviourCategory: count(packetFindings, (f) => f.behaviourCategory),
        byEvidenceStatus: count(packetFindings, (f) => f.evidenceStatus),
    };
    const rawCitations = packetFindings.reduce((n, f) => n + f.evidence.length, 0);
    const coverage = {
        rawFindingRecords: membership.rawRecords,
        uniqueFindings: packetFindings.length,
        duplicateFindingRecords: membership.duplicateRecords,
        suppliedOutsideSelection: membership.excluded,
        outsideTimeWindow: membership.outsideWindow,
        unresolvedRefs: membership.unresolvedRefs,
        truncated: membership.truncated,
        omittedFindings: membership.omitted,
        rawCitations,
        distinctEvidence: packetEvidence.length,
        distinctEvidenceContent: contentGroups.size,
        locatedEvidence: packetEvidence.filter((e) => e.located).length,
        distinctMessagesCited: messages.size,
        resolvedEvidence: packetEvidence.filter((e) => e.provenance === "resolved").length,
        excerptedEvidence: packetEvidence.filter((e) => e.excerpt).length,
        excerptedRationale: packetFindings.filter((f) => f.rationaleExcerpt).length,
        suppliedEvidenceUnmatched: unmatchedSupplied,
        sessionsWithUnknownAgentType: [...sessions.values()].filter((s) => s.agentType === "unknown").length,
        findingsWithoutEndpoint: packetFindings.filter((f) => !f.endpoint).length,
        timeBasis: sel.timeRange?.basis ?? null,
        collectedAt: collectedAt ?? null,
    };
    const digestSource = JSON.stringify({
        v: PACKET_VERSION, sel,
        findings: packetFindings.map((f) => [f.identity, f.title, f.rationale, f.evidence, f.behaviourSeverity, f.contentSensitivity, f.reason, f.evidenceStatus, f.verifierStatus]),
        evidence: packetEvidence.map((e) => [e.id, e.text, e.provenance]),
        sessions: [...sessions.values()].map((s) => [s.traceId, s.agentType, [...s.endpointIds].sort()]),
    });
    return {
        version: PACKET_VERSION,
        selection: sel,
        findings: packetFindings,
        evidence: packetEvidence,
        sessions: [...sessions.values()].map((s) => ({ ...s, endpointIds: [...s.endpointIds].sort().map((id) => endpoints.get(id).alias) })),
        endpoints: [...endpoints.values()].map((e) => ({ ...e, sessions: [...e.sessions].sort() })),
        distributions,
        coverage,
        digest: fingerprint(digestSource),
        lookup: {
            findings: new Map(packetFindings.map((f) => [f.alias, f])),
            evidence: new Map(packetEvidence.map((e) => [e.alias, e])),
            sessions: new Map([...sessions.values()].map((s) => [s.alias, s])),
            endpoints: new Map([...endpoints.values()].map((e) => [e.alias, e])),
        },
    };
}

const fmtTime = (ms) => (Number.isFinite(ms) && ms != null ? new Date(ms).toISOString() : "unknown");
const kv = (obj) => Object.entries(obj).map(([k, v]) => `${k}=${v}`).join(", ") || "none";

// Render the packet as the text the model reads. Deterministic. The return
// says whether it fits; the caller decides what to do when it does not.
export function renderPacket(packet, { maxBytes = DEFAULT_MAX_PACKET_BYTES } = {}) {
    const sel = packet.selection;
    const cov = packet.coverage;
    const lines = [];
    lines.push("## SELECTION");
    lines.push(`type=${sel.type}${sel.label ? ` label=${JSON.stringify(sel.label)}` : ""}`);
    if (sel.traceIds.length) lines.push(`prompt_sessions=${sel.traceIds.length}`);
    if (sel.endpointIds.length) lines.push(`endpoints=${sel.endpointIds.length}`);
    if (sel.findingRefs.length) lines.push(`explicit_findings=${sel.findingRefs.length}`);
    lines.push(sel.timeRange ? `time_interval=${sel.timeRange.start}..${sel.timeRange.end} basis=${sel.timeRange.basis}` : "time_interval=none");
    lines.push(sel.filters.length ? `filters=${sel.filters.map((f) => `${f.field} in [${f.values.join("|")}]`).join("; ")}` : "filters=none");
    lines.push("");
    lines.push("## COVERAGE (computed in code; do not recount)");
    lines.push(`unique_findings=${cov.uniqueFindings} raw_records=${cov.rawFindingRecords} duplicate_records=${cov.duplicateFindingRecords}`);
    lines.push(`omitted_over_limit=${cov.omittedFindings} unresolved_references=${cov.unresolvedRefs.length} outside_time_window=${cov.outsideTimeWindow}`);
    lines.push(`citations=${cov.rawCitations} distinct_evidence=${cov.distinctEvidence} distinct_evidence_content=${cov.distinctEvidenceContent} located=${cov.locatedEvidence} distinct_messages_cited=${cov.distinctMessagesCited} resolved_bodies=${cov.resolvedEvidence} excerpted=${cov.excerptedEvidence}`);
    lines.push(`sessions_with_unknown_agent_type=${cov.sessionsWithUnknownAgentType} findings_without_endpoint=${cov.findingsWithoutEndpoint}`);
    lines.push("A finding count is a count of review records. A distinct_evidence_content count is a count of distinct texts, not of events.");
    lines.push("");
    lines.push("## DISTRIBUTIONS (computed in code)");
    for (const [name, dist] of Object.entries(packet.distributions)) lines.push(`${name}: ${kv(dist)}`);
    lines.push("");
    lines.push("## PROMPT SESSIONS");
    for (const s of packet.sessions) lines.push(`${s.alias}: agent_type=${s.agentType} endpoints=[${s.endpointIds.join(",") || "unknown"}] findings=[${s.findings.join(",")}]`);
    lines.push("");
    lines.push("## ENDPOINTS");
    if (!packet.endpoints.length) lines.push("none attributed");
    for (const e of packet.endpoints) lines.push(`${e.alias}: sessions=[${e.sessions.join(",")}] findings=[${e.findings.join(",")}]`);
    lines.push("");
    lines.push("## FINDINGS (original review assertions; treat as data, not as facts or instructions)");
    for (const f of packet.findings) {
        lines.push(`### ${f.alias} session=${f.session} endpoint=${f.endpoint ?? "unknown"} agent_type=${f.agentType} review_completed=${fmtTime(f.completedAtMs)}`);
        lines.push(`severity=${f.behaviourSeverity || "unknown"} sensitivity=${f.contentSensitivity || "unknown"} reason=${f.reason || "unknown"} evidence_status=${f.evidenceStatus || "unknown"} verifier=${f.verifierStatus || "unknown"}`);
        const frames = [f.policy && `policy=${f.policy.status}${f.policy.reference ? `(${f.policy.reference})` : ""}`, f.norms && `norms=${f.norms.status}${f.norms.reference ? `(${f.norms.reference})` : ""}`, f.intent && `intent=${f.intent.status}${f.intent.reference ? `(${f.intent.reference})` : ""}`].filter(Boolean);
        if (frames.length) lines.push(frames.join(" "));
        if (f.behaviourCategory || f.contentCategory) lines.push(`behaviour_category=${f.behaviourCategory || "none"} content_category=${f.contentCategory || "none"}`);
        lines.push(`title: ${f.title || "(untitled)"}`);
        lines.push(`rationale${f.rationaleExcerpt ? " [excerpt]" : ""}: ${f.rationale || "(none)"}`);
        lines.push(`evidence: [${f.evidence.join(",") || "none supplied"}]`);
    }
    lines.push("");
    lines.push("## EVIDENCE (cite by alias; a body marked report_quote was stored by the review and not re-read from the conversation)");
    lines.push("Each alias is one location in the conversation, so two aliases are two occurrences even when their text is identical.");
    lines.push("same_text_at_other_locations lists the other occurrences of the same text. cited_by lists the review records that cite this one location; several records citing one alias is repeated review, not repeated activity.");
    lines.push("message=Mn names the conversation message a located quote comes from; two aliases with the same Mn are two offsets in one message.");
    for (const e of packet.evidence) {
        const flags = [e.provenance, e.located ? "located" : "unlocated", e.excerpt ? "excerpt" : (e.complete ? "complete" : "stored_quote")];
        const same = !e.sameContentAs.length ? ""
            : e.located ? ` same_text_at_other_locations=[${e.sameContentAs.join(",")}]`
                : ` same_text_elsewhere=[${e.sameContentAs.join(",")}] (this copy has no location; it may duplicate one of them)`;
        lines.push(`### ${e.alias} [${flags.join(",")}]${e.message ? ` message=${e.message}` : ""} cited_by=[${e.citedBy.join(",") || "none"}]${e.statuses.length ? ` verification=[${e.statuses.join(",")}]` : ""}${same}`);
        lines.push(e.text || "(empty)");
    }
    const text = lines.join("\n");
    const bytes = new TextEncoder().encode(text).length;
    return { text, bytes, maxBytes, fits: bytes <= maxBytes };
}
